<template>
  <div>

    <div v-if="format.answerType === 'sectionLabel'">
      <div class="title mt-3 mb-2">{{ format.label }}</div>
    </div>

    <div v-if="format.answerType === 'shortText'">
      <div class="body-1 blue-grey--text darken-5">{{ format.label }}</div>
      <v-text-field
        :value="answers[index].value"
        @input="inputText({ format: format, value: $event })"
        :rules="checkRules(format.isRequired, format.answerType)"
        outlined
        dense
      >
      </v-text-field>
    </div>

    <div v-else-if="format.answerType === 'mediumText'">
      <div class="body-1 blue-grey--text darken-5">{{ format.label }}</div>
      <v-textarea
        :value="answers[index].value"
        @input="inputText({ format: format, value: $event })"
        :rules="checkRules(format.isRequired, format.answerType)"
        auto-grow
        outlined
        dense
      />
    </div>

    <div v-else-if="format.answerType === 'longText'">
      <div class="body-1 blue-grey--text darken-5">{{ format.label }}</div>
      <v-textarea
        :value="answers[index].value"
        @input="inputText({ format: format, value: $event })"
        :rules="checkRules(format.isRequired, format.answerType)"
        auto-grow
        outlined
        dense
      />
    </div>

    <div v-if="format.answerType === 'number'">
      <div class="body-1 blue-grey--text darken-5">{{ format.label }}</div>
      <v-text-field
        :value="answers[index].value"
        @input="inputText({ format: format, value: $event })"
        :rules="checkRules(format.isRequired, format.answerType)"
        type="number"
        outlined
        dense
      >
      </v-text-field>
    </div>

    <div
      v-else-if="format.answerType === 'singleSelect'"
      class="mb-5"
    >
      <div class="body-1 blue-grey--text darken-5">{{ format.label }}</div>
      <v-radio-group
        :value="answers[index].value"
        @change="inputText({ format: format, value: $event })"
        :rules="checkRules(format.isRequired, format.answerType)"
        row
      >
        <v-radio
          v-for="o in format.options"
          :key="o.value"
          :label="o.label"
          :value="o.value"
        ></v-radio>
      </v-radio-group>
    </div>

    <div
      v-else-if="format.answerType === 'multiSelect'"
      class="mb-5"
    >
      <div class="body-1 blue-grey--text darken-5">{{ format.label }}</div>
      <div class="d-flex flex-wrap">
        <v-checkbox
          v-for="o in format.options"
          :key="o.value"
          :label="o.label"
          :value="o.value"
          class="label-size mr-4"
          v-model="multiSelected"
          :input-value="inputMulti({ format: format, value: multiSelected })"
          dense
          hide-details
        ></v-checkbox>
      </div>
    </div>

    <div v-else-if="format.answerType === 'boolean'">
      <v-checkbox
        class="label-size"
        :value="answers[index].value"
        @change="inputText({ format: format, value: $event })"
        :rules="checkRules(format.isRequired, format.answerType)"
        :label="format.label"
        dense
      ></v-checkbox>
    </div>

    <div v-else-if="format.answerType === 'yyyy' || format.answerType === 'year'">
      <div class="body-1 blue-grey--text darken-5">{{ format.label }}</div>
      <v-select
        :value="answers[index].value"
        @change="inputText({ format: format, value: $event })"
        :rules="checkRules(format.isRequired, format.answerType)"
        :items="yearChoices"
        outlined
        dense
      ></v-select>
    </div>

    <div v-else-if="format.answerType === 'month'">
      <div class="body-1 blue-grey--text darken-5">{{ format.label }}</div>
      <v-menu
        :ref="`${format.key}DateMonthPicker`"
        v-model="isMonthDatePicker"
        :close-on-content-click="false"
        :nudge-right="40"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="answers[index].value"
            @change="inputText({ format: format, value: $event })"
            prepend-icon="mdi-calendar"
            outlined
            dense
            readonly
            v-bind="attrs"
            v-on="on"
          ></v-text-field>
        </template>
        <v-date-picker
          :value="answers[index].value"
          @change="inputText({ format: format, value: $event })"
          @input="isMonthDatePicker = false"
          hide-header
          scrollable
          locale="ja"
          full-width
          type="month"
          :day-format="date => new Date(date).getDate()"
        >
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            @click="isMonthDatePicker = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            text
            color="primary"
            @click="$refs[`${format.key}DateMonthPicker`].save(pickupDate)"
          >
            OK
          </v-btn>
        </v-date-picker>
      </v-menu>
    </div>

    <div v-else-if="format.answerType === 'date'">
      <div class="body-1 blue-grey--text darken-5">{{ format.label }}</div>
      <v-menu
        :ref="`${format.key}DatePicker`"
        v-model="isDatePicker"
        :close-on-content-click="false"
        :nudge-right="40"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="answers[index].value"
            @change="inputText({ format: format, value: $event })"
            prepend-icon="mdi-calendar"
            outlined
            dense
            readonly
            v-bind="attrs"
            v-on="on"
          ></v-text-field>
        </template>
        <v-date-picker
          :value="answers[index].value"
          @change="inputText({ format: format, value: $event })"
          @input="isDatePicker = false"
          hide-header
          scrollable
          locale="ja"
          full-width
          :day-format="date => new Date(date).getDate()"
        >
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            @click="isDatePicker = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            text
            color="primary"
            @click="$refs[`${format.key}DatePicker`].save(pickupDate)"
          >
            OK
          </v-btn>
        </v-date-picker>
      </v-menu>
    </div>

    <div v-else-if="format.answerType === 'mm'">
      <div class="body-1 blue-grey--text darken-5">{{ format.label }}</div>
      <v-select
        :value="answers[index].value"
        @change="inputText({ format: format, value: $event })"
        :rules="checkRules(format.isRequired, format.answerType)"
        :items="monthChoices"
        outlined
        dense
      ></v-select>
    </div>

    <div v-else-if="format.answerType === 'dd'">
      <div class="body-1 blue-grey--text darken-5">{{ format.label }}</div>
      <v-select
        :value="answers[index].value"
        @change="inputText({ format: format, value: $event })"
        :rules="checkRules(format.isRequired, format.answerType)"
        :items="dayChoices"
        outlined
        dense
      ></v-select>
    </div>

  </div>
</template>
<script lang="ts">
import Vue from 'vue';
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');
import rules from '@/service/ValidRules';
import { when } from '@/service/Switch';

import { ChildInfoFormat } from 'chaild-entity/src/component/nursery/child/types';
import { isTypeAnswer, Answer } from '../../model/TypeGuard/Children';

interface DataType {
  multiSelected: any[];
  isMonthDatePicker: boolean;
  isDatePicker: boolean;
}

export default defineComponent({
  name: 'ChildrenField',

  props: {
    format: {
      type: Object,
      default: {},
    },
    answers: {
      type: Array,
      default: () => ([]),
    },
    index: {
      type: Number,
      default: 0,
    },
  },

  data:(): DataType => ({
    multiSelected: [],
    isMonthDatePicker: false,
    isDatePicker: false,
  }),

  computed: {
    rules() {
      return rules;
    },
    yearChoices() {
      const choices: Array<{text: string, value: string}> = [];
      for(let i = 1960; i <= Number(dayjs().format('YYYY')); i++) {
        choices.push({
          text: `${i}年`,
          value: `${i}`,
        });
      }
      return choices;
    },
    monthChoices() {
      const choices: Array<{text: string, value: string}> = [];
      for(let i = 1; i <= 12; i++) {
        choices.push({
          text: `${i}月`,
          value: `${i}`,
        });
      }
      return choices;
    },
    dayChoices() {
      const choices: Array<{text: string, value: string}> = [];
      for(let i = 1; i <= 31; i++) {
        choices.push({
          text: `${i}日`,
          value: `${i}`,
        });
      }
      return choices;
    },
  },

  methods: {
    checkRules(isRequired: boolean, answerType: string) {
      const ruleArray: any[] = [];
      if (isRequired) {
        ruleArray.push(this.rules.required);
      }
      when(answerType)
        .on((v) => v === 'shortText', () => ruleArray.push(this.rules.counter100))
        .on((v) => v === 'mediumText', () => ruleArray.push(this.rules.counter500))
        .on((v) => v === 'longText', () => ruleArray.push(this.rules.counter1000))
        .on((v) => v === 'number', () => ruleArray.push(this.rules.typeNumber));
      return ruleArray;
    },
    inputText({ format, value }: {
      format: ChildInfoFormat;
      value: any;
    }) {
      this.$emit('inputValue', {
        format, value,
      });
    },
    changeValue({ format, value }: {
      format: ChildInfoFormat;
      value: any;
    }) {
      this.$emit('inputValue', {
        format, value,
      });
    },
    inputMulti({ format, value }: {
      format: ChildInfoFormat;
      value: any;
    }) {
      this.$emit('inputValue', {
        format, value,
      });
    },
  },

  mounted() {
    const index = this.index;
    if (
      this.answers[index] &&
      typeof this.answers[index] === 'object'
    ) {
      // console.log(this.answers[index]);
      if (isTypeAnswer(this.answers[index])) {
        const answer = this.answers[index] as Answer;
        if (
          answer.value &&
          Array.isArray(answer.value)
        ) {
          this.multiSelected = answer.value as any[];
        }
      }
    }
  },

});
</script>
<style lang="scss" scoped>

</style>
